import { FC, useCallback, useState } from 'react';
import React from 'react';

import { useRouter } from 'expo-router';
import { StyleSheet } from 'react-native';

import ActionButton, { ActionButtonVariants } from 'components/action-button';
import { GeolocationDeniedModal } from 'components/geolocation-denied-modal';
import { StaticImage, StaticImages } from 'components/picture/static-image';
import { Box, Typography } from 'components/ui';
import { useIsDesktopBreakpoint, useIsDesktopLargeBreakpoint } from 'hooks/use-media-query';
import { useGeolocation } from 'state/geolocation';
import { primitive, spacing } from 'styles/constants';
import { isNative } from 'utils/environment';
import { routes } from 'utils/routing';

import { useLoyaltyWalmart } from '../use-loyalty-walmart';
import { WalmartLogo } from '../walmart-logo';

import {
  GeneralBottomContainer,
  GeneralTopContainer,
  InnerBottomContainer,
  InnerGrayContainer,
  InnerWhiteContainer,
  LeftButtonOne,
  LeftButtonThree,
  LeftButtonTwo,
  RightButton,
  WalmartLogoContainer,
} from './styled';

export const WalmartLocation: FC<React.PropsWithChildren<{}>> = () => {
  const { isPermissionPrompt, isPermissionDenied, setPermissionChecking } = useGeolocation();
  const [geolocationDeniedModalOpen, setGeolocationDeniedModalOpen] = useState(false);
  const dismissModal = () => setGeolocationDeniedModalOpen(false);
  const { data, loading } = useLoyaltyWalmart();
  const router = useRouter();
  const isDesktop = useIsDesktopBreakpoint();
  const isDesktopLarge = useIsDesktopLargeBreakpoint();

  const handleGeolocationPrompt = useCallback(() => {
    if (isPermissionDenied) {
      setGeolocationDeniedModalOpen(true);
    } else if (isPermissionPrompt) {
      setPermissionChecking();
    }
    router.replace(`${routes.loyaltyOfferList}?success=true`);
  }, [router, isPermissionDenied, isPermissionPrompt, setPermissionChecking]);

  if (loading) {
    return null;
  }

  return (
    <>
      <Box style={styles.root}>
        <Box
          style={[
            styles.heroContainer,
            isNative ? styles.heroContainerNative : isDesktop && styles.heroContainerDesktop,
          ]}
        >
          <GeneralTopContainer isNative={isNative}>
            <Box>
              <LeftButtonOne />
              <LeftButtonTwo />
              <LeftButtonThree />
            </Box>
            <InnerWhiteContainer>
              <InnerGrayContainer>
                <StaticImage
                  borderTopLeftRadius={30}
                  borderTopRightRadius={30}
                  height={385}
                  width="full"
                  source={StaticImages.WalmartLocation}
                  alt={'BK Image'}
                ></StaticImage>
                <WalmartLogoContainer>
                  <WalmartLogo image={data?.logo?.locale} />
                </WalmartLogoContainer>
              </InnerGrayContainer>
            </InnerWhiteContainer>
            <RightButton />
          </GeneralTopContainer>
        </Box>
        <Box style={[styles.bottomContainer, isNative && styles.bottomContainerNative]}>
          <GeneralBottomContainer isNative={isNative}>
            <InnerBottomContainer>
              <Typography
                variant="headerOne"
                fontSize={isDesktopLarge ? 30 : undefined}
                lineHeight={isDesktopLarge ? 36 : undefined}
                style={styles.title}
              >
                {data?.location?.title}
              </Typography>
              <Typography style={styles.description}>{data?.location?.description}</Typography>
              <ActionButton onPress={handleGeolocationPrompt} _text={{ fontSize: 'md' }} mb="$4">
                {data?.location?.shareLocationButton?.buttonText?.locale}
              </ActionButton>
              <ActionButton
                variant={ActionButtonVariants.TEXT_ONLY}
                onPress={() => router.replace(`${routes.loyaltyOfferList}?success=true`)}
              >
                {data?.location?.notShareLocationButton?.buttonText?.locale}
              </ActionButton>
            </InnerBottomContainer>
          </GeneralBottomContainer>
        </Box>
      </Box>
      <GeolocationDeniedModal isOpen={geolocationDeniedModalOpen} dismissModal={dismissModal} />
    </>
  );
};

const styles = StyleSheet.create({
  root: {
    backgroundColor: primitive.$white,
    flex: 1,
  },
  heroContainer: {
    backgroundColor: primitive.$white,
    height: 435,
  },
  heroContainerDesktop: {
    height: 450,
  },
  heroContainerNative: {
    height: 470,
  },
  bottomContainer: {
    backgroundColor: primitive.$white,
  },
  bottomContainerNative: {
    height: 405,
  },
  title: {
    textAlign: 'center',
  },
  description: {
    marginBottom: spacing.$8,
    textAlign: 'center',
  },
});
